import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BtButton from "../../../ts-components/bt-button/bt-button";
import { ServiceEmailPreview, EmailPreviewType, EmailPreviewParams } from "../../../services/service-email-preview";

type Props = {
  type: EmailPreviewType,
  params: EmailPreviewParams,
  height?: number,
};

// Live preview of an outgoing email, rendered by the backend through the real
// Handlebars template (POST /email/preview) so it can never drift from what
// is actually sent. Refetches debounced as the draft values change; a stale
// response never overwrites a newer one, and a failed refresh shows an error
// instead of silently keeping the previous render.
const EmailPreview: FC<Props> = ({ type, params, height = 900 }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'emailPreview' });
  const [html, setHtml] = useState("");
  const [failed, setFailed] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const paramsJson = JSON.stringify(params);
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const timer = setTimeout(async () => {
      try {
        const preview = await ServiceEmailPreview.getPreview(type, JSON.parse(paramsJson));
        if (seq === requestSeq.current) {
          setHtml(preview.html);
          setFailed(false);
        }
      } catch (error) {
        console.error("Failed to render the email preview", error);
        if (seq === requestSeq.current) setFailed(true);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [type, paramsJson, retryToken]);

  if (failed) {
    return (
      <div style={{
        border: "1px solid var(--gray-300)", borderRadius: "6px", background: "#F3F4F6",
        padding: "24px", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
      }}>
        <span>{t('error', 'Förhandsgranskningen kunde inte uppdateras.')}</span>
        <BtButton class="white" size="xxs" onClick={() => setRetryToken(token => token + 1)}>
          {t('retry', 'Försök igen')}
        </BtButton>
      </div>
    );
  }

  return (
    <iframe
      title="email-preview"
      srcDoc={html}
      // sandbox="" is load-bearing: the document can contain colleague-stored
      // editor HTML, and the fully sandboxed frame (opaque origin, no scripts,
      // no forms, no top navigation) is what keeps that inert. Never add
      // allow-* values here.
      sandbox=""
      style={{
        width: "100%",
        height,
        border: "1px solid var(--gray-300)",
        borderRadius: "6px",
        background: "#F3F4F6",
        display: "block",
      }}
    />
  );
};

export default EmailPreview;
